import { usePage } from "@inertiajs/react"
import { useCallback } from "react"

import { CHECKOUT_TOUR_SECTIONS } from "@/components/demo-tour/checkout-sections"
import type { TourPage } from "@/components/demo-tour/sections"
import TourEngine from "@/components/demo-tour/tour-engine"
import { planMarketingAnalysisCheckoutPath } from "@/routes"

interface CheckoutTourPageProps {
  mock_checkout?: boolean
  current_account?: { uid?: string }
  marketing_analysis?: { uid?: string }
  [key: string]: unknown
}

/** Standalone two-step tour on the demo (mock) checkout page — no checklist
 *  chrome, no server persistence: it plays on every demo checkout visit.
 *  mock_checkout is only ever true for demo agencies/analyses, so it doubles
 *  as the demo gate. */
export default function CheckoutTourController() {
  const page = usePage<CheckoutTourPageProps>()
  const agencyUid = page.props.current_account?.uid
  const analysisUid = page.props.marketing_analysis?.uid

  const checkoutPath =
    agencyUid && analysisUid
      ? planMarketingAnalysisCheckoutPath(agencyUid, analysisUid)
      : null

  const pathForPage = useCallback(
    (target: TourPage): string | null =>
      target.kind === "checkout" ? checkoutPath : null,
    [checkoutPath],
  )

  const pageMatches = useCallback(
    (target: TourPage, path: string): boolean =>
      target.kind === "checkout" &&
      checkoutPath !== null &&
      path === checkoutPath,
    [checkoutPath],
  )

  const isAutoStartPath = useCallback(
    (path: string): boolean => checkoutPath !== null && path === checkoutPath,
    [checkoutPath],
  )

  const enabled = Boolean(page.props.mock_checkout && checkoutPath)
  if (!enabled) return null

  return (
    <TourEngine
      sections={CHECKOUT_TOUR_SECTIONS}
      savedState={null}
      pathForPage={pathForPage}
      pageMatches={pageMatches}
      isAutoStartPath={isAutoStartPath}
      chrome="none"
    />
  )
}
